import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ShoppingCart, Plus, QrCode, RefreshCw, Loader2, Key, Copy, Eye, EyeOff, AlertCircle, Unlock, Trash2, Edit, X, Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

// Demo API Key for testing (works with any cart in test mode)
const DEMO_API_KEY = 'demo_test_key_smartcaddy_2025';

const AdminCarts = () => {
  const { api } = useAuth();
  const [carts, setCarts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialog, setEditDialog] = useState({ open: false, cart: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, cart: null });
  const [apiKeyDialog, setApiKeyDialog] = useState({ open: false, cart: null, apiKey: null, loading: false });
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    cart_code: '',
    store_id: 'store_001',
  });
  const [editFormData, setEditFormData] = useState({
    cart_code: '',
    store_id: '',
    status: '',
  });
  const [showDemoKey, setShowDemoKey] = useState(false);

  useEffect(() => {
    loadCarts();
  }, []);

  const loadCarts = async () => {
    try {
      const response = await api.get('/carts');
      setCarts(response.data);
    } catch (error) {
      toast.error('Failed to load carts');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCart = async () => {
    if (!formData.cart_code) {
      toast.error('Please enter a cart code');
      return;
    }

    setSaving(true);
    try {
      await api.post('/carts', formData);
      toast.success('Cart created successfully');
      setDialogOpen(false);
      setFormData({ cart_code: '', store_id: 'store_001' });
      loadCarts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create cart');
    } finally {
      setSaving(false);
    }
  };

  const handleEditCart = (cart) => {
    setEditFormData({
      cart_code: cart.cart_code,
      store_id: cart.store_id,
      status: cart.status,
    });
    setEditDialog({ open: true, cart });
  };

  const handleUpdateCart = async () => {
    if (!editFormData.cart_code) {
      toast.error('Please enter a cart code');
      return;
    }

    setSaving(true);
    try {
      await api.patch(`/carts/${editDialog.cart.id}`, {
        cart_code: editFormData.cart_code,
        store_id: editFormData.store_id,
        status: editFormData.status,
      });
      toast.success('Cart updated successfully');
      setEditDialog({ open: false, cart: null });
      loadCarts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update cart');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCart = async () => {
    setSaving(true);
    try {
      await api.delete(`/carts/${deleteDialog.cart.id}`);
      toast.success('Cart deleted successfully');
      setDeleteDialog({ open: false, cart: null });
      loadCarts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete cart');
    } finally {
      setSaving(false);
    }
  };

  const handleViewApiKey = async (cart) => {
    setApiKeyDialog({ open: true, cart, apiKey: null, loading: true });
    try {
      // API key is already in cart data
      setApiKeyDialog({ open: true, cart, apiKey: cart.api_key, loading: false });
    } catch (error) {
      toast.error('Failed to load API key');
      setApiKeyDialog({ open: false, cart: null, apiKey: null, loading: false });
    }
  };

  const handleGenerateQrId = async (cart) => {
    try {
      const response = await api.post(`/carts/${cart.id}/generate-qr-id`);
      toast.success(`QR ID generated: ${response.data.qr_id.slice(0, 8)}...`);
      loadCarts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate QR ID');
    }
  };

  const handleCopyApiKey = (key) => {
    navigator.clipboard.writeText(key);
    toast.success('API key copied to clipboard');
  };

  const handleReleaseCart = async (cartCode) => {
    try {
      const response = await api.post(`/carts/${cartCode}/release`);
      toast.success(`Cart ${cartCode} released. ${response.data.sessions_expired} session(s) expired.`);
      loadCarts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to release cart');
    }
  };

  const handleUpdateStatus = async (cartId, status) => {
    try {
      await api.patch(`/carts/${cartId}/status?status=${status}`);
      toast.success('Cart status updated');
      loadCarts();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'available':
        return 'bg-emerald-100 text-emerald-800';
      case 'in_use':
        return 'bg-sky-100 text-sky-800';
      case 'maintenance':
        return 'bg-amber-100 text-amber-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold font-['Outfit'] text-slate-800">Smart Carts</h1>
          <p className="text-slate-500">Manage your store&apos;s smart shopping carts</p>
        </div>
        <Button data-testid="add-cart-btn" onClick={() => setDialogOpen(true)} className="bg-teal-500 hover:bg-teal-600 text-white">
          <Plus className="w-4 h-4 mr-2" />
          Add Cart
        </Button>
      </div>

      {/* Demo API Key Card */}
      <Card className="mb-6 border-dashed border-2 border-teal-300 bg-teal-50/50">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-teal-100 rounded-lg">
                <Key className="w-5 h-5 text-teal-600" />
              </div>
              <div>
                <h3 className="font-semibold text-teal-900">Demo API Key for Testing</h3>
                <p className="text-sm text-teal-700">
                  Use this universal key to test RFID events with any cart (test mode only)
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-teal-200">
                <code className="font-mono text-sm text-slate-700">
                  {showDemoKey ? DEMO_API_KEY : '••••••••••••••••••••'}
                </code>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-6 w-6 text-slate-500 hover:text-slate-700"
                  onClick={() => setShowDemoKey(!showDemoKey)}
                >
                  {showDemoKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                className="border-teal-300 text-teal-700 hover:bg-teal-50"
                onClick={() => handleCopyApiKey(DEMO_API_KEY)}
              >
                <Copy className="w-4 h-4 mr-1" />
                Copy
              </Button>
            </div>
          </div>
          <div className="mt-3 p-3 bg-white/60 rounded-lg">
            <p className="text-xs text-teal-800 font-mono">
              curl -X POST &quot;{process.env.REACT_APP_BACKEND_URL || 'https://your-domain.com'}/api/hardware/rfid&quot; \<br/>
              &nbsp;&nbsp;-H &quot;X-Cart-API-Key: {DEMO_API_KEY}&quot; \<br/>
              &nbsp;&nbsp;-H &quot;Content-Type: application/json&quot; \<br/>
              &nbsp;&nbsp;-d &apos;{'{'}cart_code&quot;: &quot;CART001&quot;, &quot;rfid_tag&quot;: &quot;RFID001&quot;, &quot;action&quot;: &quot;ADD&quot;, &quot;quantity&quot;: 1{'}'}&apos;
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Carts</p>
                <p className="text-2xl font-bold text-slate-800">{carts.length}</p>
              </div>
              <ShoppingCart className="w-8 h-8 text-slate-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Available</p>
                <p className="text-2xl font-bold text-emerald-600">
                  {carts.filter(c => c.status === 'available').length}
                </p>
              </div>
              <div className="w-3 h-3 rounded-full bg-emerald-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">In Use</p>
                <p className="text-2xl font-bold text-sky-600">
                  {carts.filter(c => c.status === 'in_use').length}
                </p>
              </div>
              <div className="w-3 h-3 rounded-full bg-sky-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Carts Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-48 bg-slate-100 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : carts.length === 0 ? (
        <div className="text-center py-16">
          <ShoppingCart className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">No carts configured yet</p>
          <Button className="mt-4 bg-teal-500 hover:bg-teal-600" onClick={() => setDialogOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Add Your First Cart
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {carts.map((cart, index) => (
            <motion.div
              key={cart.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card className={`border-slate-200 ${cart.status === 'in_use' ? 'border-sky-300 bg-sky-50/30' : ''}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-mono text-lg text-slate-800">{cart.cart_code}</CardTitle>
                    <Badge className={getStatusColor(cart.status)}>
                      {cart.status.replace('_', ' ')}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-center py-6 bg-slate-100 rounded-lg mb-4">
                    <QrCode className="w-20 h-20 text-slate-400" />
                  </div>
                  
                  <div className="text-sm text-slate-500 mb-4 space-y-1">
                    <p className="flex items-center gap-2">
                      <Key className="w-3 h-3" />
                      API Key: <span className="font-mono">{cart.api_key.slice(0, 12)}...</span>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-6 px-2 text-teal-600 hover:text-teal-700 hover:bg-teal-50"
                        onClick={() => handleViewApiKey(cart)}
                      >
                        <Eye className="w-3 h-3 mr-1" />
                        View
                      </Button>
                    </p>
                    <p className="flex items-center gap-2">
                      <QrCode className="w-3 h-3" />
                      QR ID: {cart.qr_id ? (
                        <>
                          <span className="font-mono text-xs">{cart.qr_id.slice(0, 8)}...</span>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-5 px-1 text-slate-500 hover:text-slate-700"
                            onClick={() => {
                              navigator.clipboard.writeText(cart.qr_id);
                              toast.success('QR ID copied');
                            }}
                          >
                            <Copy className="w-3 h-3" />
                          </Button>
                        </>
                      ) : (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-5 px-2 text-teal-600 hover:text-teal-700 hover:bg-teal-50"
                          onClick={() => handleGenerateQrId(cart)}
                        >
                          Generate
                        </Button>
                      )}
                    </p>
                    <p>Store: {cart.store_id}</p>
                  </div>

                  <div className="flex gap-2">
                    <Select 
                      value={cart.status}
                      onValueChange={(value) => handleUpdateStatus(cart.id, value)}
                    >
                      <SelectTrigger className="flex-1 border-slate-200">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="available">Available</SelectItem>
                        <SelectItem value="in_use">In Use</SelectItem>
                        <SelectItem value="maintenance">Maintenance</SelectItem>
                      </SelectContent>
                    </Select>
                    {cart.status === 'in_use' && (
                      <Button 
                        variant="outline" 
                        size="icon"
                        className="border-orange-300 text-orange-600 hover:bg-orange-50"
                        onClick={() => handleReleaseCart(cart.cart_code)}
                        title="Force release cart"
                      >
                        <Unlock className="w-4 h-4" />
                      </Button>
                    )}
                    <Button 
                      variant="outline" 
                      size="icon"
                      className="border-slate-200 text-slate-600 hover:bg-slate-50"
                      onClick={() => handleEditCart(cart)}
                      title="Edit cart"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="icon"
                      className="border-red-200 text-red-600 hover:bg-red-50"
                      onClick={() => setDeleteDialog({ open: true, cart })}
                      title="Delete cart"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {/* Add Cart Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-slate-800">Add New Cart</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="cart_code" className="text-slate-700">Cart Code *</Label>
              <Input
                id="cart_code"
                value={formData.cart_code}
                onChange={(e) => setFormData({ ...formData, cart_code: e.target.value.toUpperCase() })}
                placeholder="e.g., CART004"
                className="uppercase border-slate-200"
              />
              <p className="text-xs text-slate-500">
                This code will be encoded in the QR code on the cart
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="store_id" className="text-slate-700">Store</Label>
              <Select 
                value={formData.store_id}
                onValueChange={(v) => setFormData({ ...formData, store_id: v })}
              >
                <SelectTrigger className="border-slate-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="store_001">Store 001 (Default)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-slate-200">Cancel</Button>
            <Button onClick={handleAddCart} disabled={saving} className="bg-teal-500 hover:bg-teal-600 text-white">
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Cart'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Cart Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => setEditDialog({ ...editDialog, open })}>
        <DialogContent className="bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-slate-800">Edit Cart</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit_cart_code" className="text-slate-700">Cart Code *</Label>
              <Input
                id="edit_cart_code"
                value={editFormData.cart_code}
                onChange={(e) => setEditFormData({ ...editFormData, cart_code: e.target.value.toUpperCase() })}
                placeholder="e.g., CART004"
                className="uppercase border-slate-200"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_store_id" className="text-slate-700">Store</Label>
              <Select 
                value={editFormData.store_id}
                onValueChange={(v) => setEditFormData({ ...editFormData, store_id: v })}
              >
                <SelectTrigger className="border-slate-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="store_001">Store 001 (Default)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_status" className="text-slate-700">Status</Label>
              <Select 
                value={editFormData.status}
                onValueChange={(v) => setEditFormData({ ...editFormData, status: v })}
              >
                <SelectTrigger className="border-slate-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="available">Available</SelectItem>
                  <SelectItem value="in_use">In Use</SelectItem>
                  <SelectItem value="maintenance">Maintenance</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog({ open: false, cart: null })} className="border-slate-200">Cancel</Button>
            <Button onClick={handleUpdateCart} disabled={saving} className="bg-teal-500 hover:bg-teal-600 text-white">
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Cart Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <DialogContent className="bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-red-600">Delete Cart</DialogTitle>
            <DialogDescription className="text-slate-500">
              Are you sure you want to delete cart <strong>{deleteDialog.cart?.cart_code}</strong>? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, cart: null })} className="border-slate-200">Cancel</Button>
            <Button onClick={handleDeleteCart} disabled={saving} className="bg-red-500 hover:bg-red-600 text-white">
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Cart
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View API Key Dialog */}
      <Dialog open={apiKeyDialog.open} onOpenChange={(open) => setApiKeyDialog(prev => ({ ...prev, open }))}>
        <DialogContent className="bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-800">
              <Key className="w-5 h-5 text-teal-600" />
              API Key for {apiKeyDialog.cart?.cart_code}
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              Use this key in the X-Cart-API-Key header for RFID hardware events
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            {apiKeyDialog.loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-teal-500" />
              </div>
            ) : apiKeyDialog.apiKey ? (
              <div className="space-y-4">
                <div className="p-4 bg-slate-100 rounded-lg">
                  <Label className="text-xs text-slate-500">Full API Key</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <code className="flex-1 font-mono text-sm break-all text-slate-700">{apiKeyDialog.apiKey}</code>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="border-slate-200"
                      onClick={() => handleCopyApiKey(apiKeyDialog.apiKey)}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <p>Keep this key secure! It authenticates your hardware to send RFID events.</p>
                  </div>
                </div>

                <div className="p-3 bg-slate-100 rounded-lg">
                  <Label className="text-xs text-slate-500">Example curl command</Label>
                  <pre className="mt-1 text-xs overflow-x-auto whitespace-pre-wrap text-slate-700">
{`curl -X POST "${process.env.REACT_APP_BACKEND_URL || 'https://your-domain.com'}/api/hardware/rfid" \\
  -H "X-Cart-API-Key: ${apiKeyDialog.apiKey}" \\
  -H "Content-Type: application/json" \\
  -d '{"cart_code": "${apiKeyDialog.cart?.cart_code}", "rfid_tag": "RFID001", "action": "ADD", "quantity": 1}'`}
                  </pre>
                </div>
              </div>
            ) : (
              <p className="text-center text-slate-500 py-8">Failed to load API key</p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setApiKeyDialog({ open: false, cart: null, apiKey: null, loading: false })} className="border-slate-200">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCarts;
